import { JsonForms } from "@jsonforms/react";
import { vanillaCells, vanillaRenderers } from "@jsonforms/vanilla-renderers";
import { Box, Grid, Skeleton, Typography } from "@mui/material";
import { JSONObject } from "@spring-global/spring-datasources/lib/src/Types";
import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { ButtonToolbar, Icon, IconButton, Message } from "rsuite";
import GridRenderer, {
  gridRendererTester,
} from "../CustomRenders/GridRenderer";
import NotesListRenderer, {
  notesListTester,
} from "../CustomRenders/NotesListRenderer";
import StatusBadgeRenderer, {
  statusBadgeRendererTester,
} from "../CustomRenders/StatusBadgeRenderer";
import ToggleRenderer, {
  toggleRendererTester,
} from "../CustomRenders/ToggleRenderer";
import AttachmentPreviewRenderer, {
  attachmentPreviewTester,
} from "../CustomRenders/AttachmentPreviewRenderer";
import TimelineRenderer, {
  timelineRendererTester,
} from "../CustomRenders/TimelineRenderer";
import ValueBadgeRenderer, {
  valueBadgeRendererTester,
} from "../CustomRenders/ValueBadgeRenderer";
import CurrencyRenderer, {
  currencyRendererTester,
} from "../CustomRenders/CurrencyRenderer";
import NumberRenderer, {
  numberRendererTester,
} from "../CustomRenders/NumberRenderer";
import { DataHubClient } from "../DataSet/DataHubClient";
import { useDataset } from "../DataSet/DatasetProvider";
import { ItemForDetailParams } from "../types";
import "./DynamicDetailViewer.scss";

interface IDynamicDetailViewerProps {
  item: ItemForDetailParams | null;
  className?: string;
}

const DynamicDetailViewer = (props: IDynamicDetailViewerProps) => {
  const [loading, setLoading] = useState(false);
  const [schema, setSchema] = useState<JSONObject>(null);
  const [uischema, setUISchema] = useState<JSONObject>(null);
  const [data, setData] = useState<JSONObject>(null);
  const [notFound, setNotFound] = useState(false);
  const { setSelectedItemForDetail, requestId } = useDataset();
  const history = useHistory();
  const requestIdRef = useRef(0);

  const SkeletonLoading = () => {
    return (
      <Box p={3}>
        <Typography variant="h6" gutterBottom>
          <Skeleton width={120} />
        </Typography>
        <Grid container spacing={3}>
          {Array.from({ length: 10 }).map((_, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Typography variant="caption">
                <Skeleton width={index % 2 === 0 ? 60 : 80} />
              </Typography>
              <Skeleton variant="rectangular" height={"2.42em"} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  const close = () => {
    setSelectedItemForDetail(null);
    setData(null);
    setSchema(null);
    setUISchema(null);
    setNotFound(false);
    setLoading(false);
    history.goBack();
  };

  useEffect(() => {
    if (!props?.item?.dataSourceId || !props?.item?.idObject) {
      setLoading(false);
      setData(null);
      setNotFound(false);
      return;
    }

    const currentRequestId = ++requestIdRef.current;

    setLoading(true);
    setNotFound(false);
    setData(null);
    setSchema(null);
    setUISchema(null);

    DataHubClient.getObjectDetail({
      publicId: props.item.dataSourceId,
      idObject: String(props.item.idObject),
      _requestId: requestId,
    })
      .then((res: JSONObject) => {
        if (currentRequestId !== requestIdRef.current) return;

        if (!res?.data) {
          setNotFound(true);
          return;
        }

        Object.keys(res.schema?.properties || {}).forEach((k) => {
          const format = res.schema.properties[k].format;
          if (
            (format === "date-time" || format === "date") &&
            res.data[k] === null
          ) {
            delete res.schema.properties[k].format;
          }
        });

        setSchema(res.schema);
        setUISchema(res.uiSchema);
        setData(res.data);
      })
      .catch((e) => {
        if (currentRequestId !== requestIdRef.current) return;
        console.error(e);
        setNotFound(true);
      })
      .finally(() => {
        if (currentRequestId !== requestIdRef.current) return;
        setLoading(false);
      });
  }, [props.item?.dataSourceId, props.item?.idObject, requestId]);

  const renderers = [
    { tester: notesListTester, renderer: NotesListRenderer },
    { tester: gridRendererTester, renderer: GridRenderer },
    { tester: statusBadgeRendererTester, renderer: StatusBadgeRenderer },
    { tester: toggleRendererTester, renderer: ToggleRenderer },
    { tester: attachmentPreviewTester, renderer: AttachmentPreviewRenderer },
    { tester: timelineRendererTester, renderer: TimelineRenderer },
    { tester: valueBadgeRendererTester, renderer: ValueBadgeRenderer },
    { tester: currencyRendererTester, renderer: CurrencyRenderer },
    { tester: numberRendererTester, renderer: NumberRenderer },
    ...vanillaRenderers,
  ];

  return (
    <div className={`DynamicDetailViewer ${props.className ?? ""}`}>
      <div className="header">
        <ButtonToolbar>
          <IconButton
            size="lg"
            appearance="subtle"
            icon={<Icon icon="chevron-left" />}
            onClick={close}
          />
        </ButtonToolbar>
        <h1>{props?.item?.title ?? "Details"}</h1>
      </div>

      {/* Loading */}
      {loading && <SkeletonLoading />}

      {/* Success */}
      {!loading && data && (
        <div className="form">
          <JsonForms
            schema={schema}
            uischema={uischema}
            data={data}
            renderers={renderers}
            cells={vanillaCells}
            validationMode="NoValidation"
            readonly
          />
        </div>
      )}

      {/* Not Found */}
      {!loading && notFound && (
        <Message
          className="message-warning"
          showIcon
          type="warning"
          title="Error 404"
          description={
            <p>
              Item Not Found
              <br />
              <ButtonToolbar>
                <IconButton
                  size="xs"
                  icon={<Icon icon="chevron-left" />}
                  onClick={close}
                >
                  Back
                </IconButton>
              </ButtonToolbar>
            </p>
          }
        />
      )}
    </div>
  );
};

export default DynamicDetailViewer;
